import { Link } from 'react-router-dom'
import { isHardDeadlinePassed, personLabel, StatusBadge } from './HRDashboard'
import { Alert, Card, Button, Spin, Typography, Tabs } from 'antd'
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import Stepper from '../components/Stepper'
import ReportHistorySection from './ReportHistorySection'
import AppraisalPdfButton from './AppraisalPdfButton'
import { formatDate } from '../lib/dateFormat'
import CompletedAppraisalRatings from './CompletedAppraisalRatings'
import { isFunctionalReviewActionable, MANAGER_DELEGATION_ENABLED, supportsIndirectDelegation } from './delegationConfig'

const { Text } = Typography

const APPROVAL_STEPS = [
  { key: 'employee', label: 'Employee', statuses: ['pending_employee', 'pending_employee_review'] },
  { key: 'approver_1', label: 'Approver 1', statuses: ['pending_manager'], assignmentField: 'approver_1' },
  { key: 'approver_2', label: 'Approver 2', statuses: ['pending_head'], assignmentField: 'approver_2' },
  { key: 'approver_3', label: 'Approver 3', statuses: ['pending_bu_head'], assignmentField: 'approver_3' },
  { key: 'approver_4', label: 'Approver 4', statuses: ['pending_approver_4'], assignmentField: 'approver_4' },
  { key: 'approver_5', label: 'Approver 5', statuses: ['pending_approver_5'], assignmentField: 'approver_5' }
]

function hasAssignedApprover(appraisal, step) {
  if (!step.assignmentField) return true
  return Boolean(appraisal?.[step.assignmentField])
}

function appraisalProgressForStatus(status, steps) {
  const index = steps.findIndex(step => step.statuses.includes(status))
  if (index >= 0) return index
  if (status === 'completed') return steps.length
  return 0
}

function AppraisalProgressTracker({ status, appraisal, compact = false }) {
  const employeeName = [appraisal?.employee_first_name, appraisal?.employee_last_name].filter(Boolean).join(' ').trim() || appraisal?.employee_email || 'Employee'
  const approverSteps = APPROVAL_STEPS
    .filter(step => step.key !== 'employee')
    .filter(step => hasAssignedApprover(appraisal, step) || step.statuses.includes(status))
    .map(step => ({
      ...step,
      name: personLabel(
        appraisal?.[`${step.key}_first_name`],
        appraisal?.[`${step.key}_last_name`],
        appraisal?.[`${step.key}_email`],
        appraisal?.[`${step.key}_position`],
        appraisal?.session_template
      )
    }))

  const stepDetails = [
    { ...APPROVAL_STEPS[0], name: employeeName },
    ...approverSteps
  ]

  if (stepDetails.length === 1) {
    stepDetails.push({
      ...APPROVAL_STEPS[1],
      name: personLabel(appraisal?.approver_1_first_name, appraisal?.approver_1_last_name, appraisal?.approver_1_email, appraisal?.approver_1_position, appraisal?.session_template) || 'Approver 1'
    })
  }

  if (stepDetails.length === 0) return null

  const activeIndex = appraisalProgressForStatus(status, stepDetails)
  const current = status === 'completed'
    ? stepDetails.length - 1
    : Math.max(activeIndex, 0)

  return (
    <Stepper
      steps={stepDetails.map(step => ({ key: step.key, label: step.label, caption: step.name }))}
      current={current}
      compact={compact}
    />
  )
}

export default function EmployeeDashboard({ appraisals = [], me, appraisalsLoading }) {
  if (appraisalsLoading) return <Spin />

  const activeAppraisals = appraisals.filter(a => a.session_status === 'active')
  const myEmployeeAppraisals = activeAppraisals.filter(a => me && a.employee === me.id)
  const myPastAppraisals = appraisals.filter(a => me && a.employee === me.id && a.session_status === 'closed')
  const pending = myEmployeeAppraisals.filter(a => ['pending_employee', 'pending_employee_review'].includes(a.status))
  const submitted = myEmployeeAppraisals.filter(a => a.employee_submitted_at && !['pending_employee', 'pending_employee_review'].includes(a.status))
  const reportReviews = activeAppraisals.filter(a => me && a.manager === me.id && a.status === 'pending_manager')
  const assignedFeedbackReviews = activeAppraisals.filter(a => a.active_functional_review_id)
  const pendingFeedbackReviews = assignedFeedbackReviews.filter(isFunctionalReviewActionable)
  const showFeedbackRequests = MANAGER_DELEGATION_ENABLED && activeAppraisals.some(
    appraisal => supportsIndirectDelegation(appraisal.session_manager_review_flow)
  )
  const showMyAppraisalCards = !me?.is_manager_with_direct_manager
  const hasPendingWork = pending.length > 0 || reportReviews.length > 0 || pendingFeedbackReviews.length > 0
  const submittedSummary = submitted.length > 0
    ? (submitted.length === 1
      ? `${submitted[0].session_name} was submitted successfully.`
      : `${submitted.length} appraisals were submitted successfully.`)
    : ''
  const workSummary = me?.is_manager_with_direct_manager
    ? `You have ${pending.length} appraisal(s) to complete and ${reportReviews.length} appraisal(s) from your direct reports to review.`
    : `You have ${pending.length} appraisal(s) to complete and ${pendingFeedbackReviews.length} feedback request(s).`
  const mergedNotification = [submittedSummary, hasPendingWork ? workSummary : ''].filter(Boolean).join(' ')
  const mergedNotificationIsWarning = hasPendingWork
  const myAppraisalsContent = showMyAppraisalCards
    ? (
      myEmployeeAppraisals.length === 0
        ? <Text type='secondary'>No appraisals assigned to you yet.</Text>
        : myEmployeeAppraisals.map(a => (
          <Card key={a.id} size='small' style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.session_name}</div>
                <div><Text type='secondary' style={{ fontSize: 12 }}>Employee ID: {a.employee_company_id || '—'}</Text></div>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  Approver: {personLabel(a.approver_1_first_name, a.approver_1_last_name, a.approver_1_email, a.approver_1_position, a.session_template)} · Dept: {a.department_name || '—'}
                </Text>
                <div><Text type='secondary' style={{ fontSize: 12 }}>Deadline: {formatDate(a.session_deadline_date) || '—'}</Text></div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 8, minWidth: 0, maxWidth: '100%' }}>
                <StatusBadge status={a.status} label={a.status_label} />
                <AppraisalProgressTracker status={a.status} appraisal={a} compact />
                <CompletedAppraisalRatings appraisal={a} />
                {['pending_employee', 'pending_employee_review'].includes(a.status)
                  ? isHardDeadlinePassed(a)
                    ? <Button size='small' disabled>Deadline Passed</Button>
                    : <Link to={`/appraisals/${a.id}/employee`}><Button className='tti-btn-primary-grey' size='small'>{a.status === 'pending_employee_review' ? 'Review Feedback' : 'Fill Out Form'}</Button></Link>
                  : <Link to={`/appraisals/${a.id}`}><Button size='small'>View</Button></Link>}
                <AppraisalPdfButton appraisalId={a.id} status={a.status} canExport={a.can_export_pdf} />
              </div>
            </div>
          </Card>
        ))
    )
    : <Text type='secondary'>No employee appraisals available.</Text>

  const feedbackRequestsContent = assignedFeedbackReviews.length > 0
    ? (
      assignedFeedbackReviews.map(appraisal => {
        const employeeName = [appraisal.employee_first_name, appraisal.employee_last_name].filter(Boolean).join(' ').trim() || appraisal.employee_email
        const isActionable = isFunctionalReviewActionable(appraisal)
        return (
          <Card key={appraisal.active_functional_review_id} size='small' style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{employeeName}</div>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  Employee ID: {appraisal.employee_company_id || '—'} · {appraisal.session_name} · {appraisal.department_name || 'No department'}
                </Text>
              </div>
              <Link to={`/appraisals/${appraisal.id}/manager/feedback/${appraisal.active_functional_review_id}`}>
                <Button type={isActionable ? 'primary' : 'default'} size='small'>
                  {isActionable ? 'Provide Feedback' : 'View Feedback'}
                </Button>
              </Link>
            </div>
          </Card>
        )
      })
    )
    : <Text type='secondary'>No feedback requests assigned to you yet.</Text>



  return (
    <>
      {(submitted.length > 0 || hasPendingWork) && (
        <Alert
          style={{ marginBottom: 16 }}
          showIcon
          type={mergedNotificationIsWarning ? 'warning' : 'success'}
          icon={mergedNotificationIsWarning ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
          message={mergedNotification || workSummary}
          description={me?.is_manager_with_direct_manager
            ? 'Your self appraisal is handled from the Employee dashboard, while direct reports are handled from the Approvals dashboard.'
            : null}
        />
      )}
      <Tabs
        defaultActiveKey='my-appraisals'
        style={{ marginTop: 12 }}
        items={[
          { key: 'my-appraisals', label: 'My Appraisals', children: myAppraisalsContent },
          showFeedbackRequests && { key: 'feedback-requests', label: 'Feedback Requests', children: feedbackRequestsContent }
        ].filter(Boolean)}
      />
    </>
  )
}
